import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './config';
import Home from './Home';
import Equity from './Equity';
import OpenPositions from './OpenPositions';
import LiveOpenPositions from './LiveOpenPositions';
import Trades from './Trades';
import Trades2 from './Trades2';
import Tasty from './Tasty';
import PMCC from './PMCC';
import Strategies from './Strategies';
import ApiDocs from './ApiDocs';
import SharedInfo from './SharedInfo';
import ETFs from './ETFs';
import ITMOTMCalculation from './ITMOTMCalculation';
import OptionROI from './OptionROI';
import ProtectiveCoveredCall from './ProtectiveCoveredCall';
import TagManagement from './TagManagement';
import AccountDisplayNameManagement from './AccountDisplayNameManagement';
import Todo from './Todo';
import AdvancedOptionsDashboard from './AdvancedOptionsDashboard';
import Earnings from './Earnings';
import Header from './Header';
import ViewingAsBanner from './ViewingAsBanner';
import CoveredCalls from './CoveredCalls';
import EquityNew from './EquityNew';
import TWR from './TWR';
import MarginSwitch from './MarginSwitch';
import Risk from './Risk';
import Sidebar from './Sidebar';

/** Redirects to Django admin (same host as API, /admin/). */
function AdminRedirect() {
  const adminUrl = API_URL.replace(/\/api\/?$/, '') + '/admin/';
  window.location.href = adminUrl;
  return <div style={{ padding: '1rem' }}>Redirecting to admin…</div>;
}

function AppRouter({ username, onLogout, onManageAccounts, onManageTags, data, loading, error, fetchData }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [enableSidebar, setEnableSidebar] = useState(false);
  const [showMargin, setShowMargin] = useState(false);
  const [infoAttentionCount, setInfoAttentionCount] = useState(0);

  const refreshInfoAttention = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/portfolio/info-items/attention-count/`);
      setInfoAttentionCount(typeof r.data?.count === 'number' ? r.data.count : 0);
    } catch {
      setInfoAttentionCount(0);
    }
  }, []);

  useEffect(() => {
    refreshInfoAttention();
  }, [refreshInfoAttention]);
  
  // Get submenu from URL params, default to appropriate values based on current path
  const getSubmenu = () => {
    const submenu = searchParams.get('submenu');
    if (location.pathname === '/open-positions') {
      return submenu || 'options';  // Default to 'options' instead of 'open-positions'
    } else if (location.pathname === '/trades') {
      return submenu || 'trades';
    } else if (location.pathname === '/equity') {
      return submenu || 'equity';
    } else if (location.pathname === '/documentation') {
      return submenu || 'api';
    } else if (location.pathname === '/tools') {
      return submenu || 'option-roi';
    } else if (location.pathname === '/strategies') {
      return submenu || 'review';
    }
    return submenu || null;
  };
  
  const currentSubmenu = getSubmenu();

  const handleSubmenuChange = (submenu) => {
    setSearchParams({ submenu });
  };

  const pageConfig = {
    '/': { title: 'Portfolio Dashboard', subtitle: 'Dashboard' },
    '/equity': { title: 'Equity', subtitle: 'Equity' },
    '/open-positions': { title: 'Open Positions', subtitle: 'Open Positions' },
    '/trades': { title: 'Trades', subtitle: 'Trades' },
    '/tasty': { title: 'Tasty', subtitle: 'Tastytrade Trades' },
    '/trades2': { title: 'Trades2 Dashboard', subtitle: 'Trades2 Dashboard' },
    '/pmcc': { title: 'PMCC', subtitle: 'PMCC' },
    '/documentation': { title: 'Documentation', subtitle: 'Documentation' },
    '/tools': { title: 'Tools', subtitle: 'Tools' },
    '/strategies': { title: 'Strategieën', subtitle: 'Trade strategie-tags' },
    '/tools/earnings': { title: 'Earnings', subtitle: 'Earnings Calendar' },
    '/todo': { title: 'Todo', subtitle: 'Todo' },
    '/settings': { title: 'Settings', subtitle: 'Settings' },
    '/tags': { title: 'Manage Tags', subtitle: 'Manage Tags' },
    '/tmp': { title: 'Advanced Dashboard', subtitle: 'Advanced Dashboard' },
    '/equity-new': { title: 'Equity', subtitle: 'Equity' }
  };

  const currentConfig = pageConfig[location.pathname] || pageConfig['/'];

  const handleSwitchUser = () => {
    // Refresh data after switching user
    if (fetchData) {
      fetchData();
    }
    // Force page reload to ensure all components refresh
    window.location.reload();
  };

  // Fetch user settings to check if sidebar should be enabled
  useEffect(() => {
    fetchUserSettings();
    
    // Refresh settings when returning to the app (e.g., from settings page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUserSettings();
      }
    };

    const handleFocus = () => {
      fetchUserSettings();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/user-settings/`);
      const settings = response.data.results || response.data;
      const userSettings = Array.isArray(settings) && settings.length > 0 
        ? settings[0] 
        : (!Array.isArray(settings) && settings ? settings : null);
      
      if (userSettings && userSettings.enable_sidebar !== null && userSettings.enable_sidebar !== undefined) {
        setEnableSidebar(userSettings.enable_sidebar);
      } else {
        setEnableSidebar(false);
      }
      setShowMargin(!!(userSettings && userSettings.show_margin));
    } catch (err) {
      console.error('Error fetching user settings for sidebar:', err);
      setEnableSidebar(false);
      setShowMargin(false);
    }
  };

  return (
    <div className={`App ${enableSidebar ? 'with-sidebar' : ''}`}>
      {enableSidebar && (
        <Sidebar 
          username={username} 
          onManageAccounts={onManageAccounts}
        />
      )}
      <ViewingAsBanner />
      <Header
        username={username}
        onLogout={onLogout}
        onManageAccounts={onManageAccounts}
        onManageTags={onManageTags}
        onSwitchUser={handleSwitchUser}
        pageTitle={currentConfig.title}
        pageSubtitle={currentConfig.subtitle}
        infoAttentionCount={infoAttentionCount}
      />
      
      {location.pathname === '/open-positions' && (
        <nav className="sub-nav">
          <div className="sub-nav-container">
            <button 
              className={`submenu-item ${currentSubmenu === 'options' || (!currentSubmenu || currentSubmenu === 'open-positions') ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('options')}
            >
              Options
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'stocks' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('stocks')}
            >
              Stocks
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'short-put-exposure' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('short-put-exposure')}
            >
              Short Put Exposure
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'rolled-options' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('rolled-options')}
            >
              Rolled Options
            </button>
          </div>
        </nav>
      )}

      {location.pathname === '/trades' && (
        <nav className="sub-nav">
          <div className="sub-nav-container">
            <button 
              className={`submenu-item ${currentSubmenu === 'trades' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('trades')}
            >
              Trades
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'group-on-symbol' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('group-on-symbol')}
            >
              Group on Symbol
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'pnl' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('pnl')}
            >
              P&amp;L
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'covered-calls' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('covered-calls')}
            >
              Covered Calls
            </button>
            <button 
              className={`submenu-item submenu-item-right ${currentSubmenu === 'info' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('info')}
            >
              Info
            </button>
          </div>
        </nav>
      )}

      {location.pathname === '/equity' && (
        <nav className="sub-nav">
          <div className="sub-nav-container">
            <button
              className={`submenu-item ${currentSubmenu === 'equity' || !currentSubmenu ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('equity')}
            >
              Equity
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'pnl' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('pnl')}
            >
              P&amp;L
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'deposits' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('deposits')}
            >
              Deposits/Withdrawals
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'twr' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('twr')}
            >
              TWR
            </button>
            {showMargin && (
              <button
                className={`submenu-item ${currentSubmenu === 'margin' ? 'active' : ''}`}
                onClick={() => handleSubmenuChange('margin')}
              >
                Margin
              </button>
            )}
            <button
              className={`submenu-item ${currentSubmenu === 'risk' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('risk')}
            >
              Risk
            </button>
          </div>
        </nav>
      )}

      {location.pathname === '/documentation' && (
        <nav className="sub-nav">
          <div className="sub-nav-container">
            <button 
              className={`submenu-item ${currentSubmenu === 'etfs' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('etfs')}
            >
              ETFs
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'api' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('api')}
            >
              API
            </button>
            <button 
              className={`submenu-item ${currentSubmenu === 'itm-otm' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('itm-otm')}
            >
              ITM/OTM Berekeningen
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'informatie' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('informatie')}
            >
              Informatie
              {infoAttentionCount > 0 ? (
                <span className="doc-nav-badge" title="Niet-geziene of bijgewerkte items">
                  {infoAttentionCount > 99 ? '99+' : infoAttentionCount}
                </span>
              ) : null}
            </button>
          </div>
        </nav>
      )}

      {location.pathname === '/tools' && (
        <nav className="sub-nav">
          <div className="sub-nav-container">
            <button 
              className={`submenu-item ${currentSubmenu === 'option-roi' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('option-roi')}
            >
              Option ROI
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'protective-covered-call' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('protective-covered-call')}
            >
              Protective Covered Call
            </button>
          </div>
        </nav>
      )}

      {location.pathname === '/strategies' && (
        <nav className="sub-nav">
          <div className="sub-nav-container">
            <button
              className={`submenu-item ${currentSubmenu === 'review' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('review')}
            >
              Review
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'confirmed' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('confirmed')}
            >
              Bevestigd
            </button>
            <button
              className={`submenu-item ${currentSubmenu === 'overview' ? 'active' : ''}`}
              onClick={() => handleSubmenuChange('overview')}
            >
              Overzicht
            </button>
          </div>
        </nav>
      )}

      <main className="App-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/equity" element={
            currentSubmenu === 'deposits' ? (
              <Equity activeSubmenu="deposits" setActiveSubmenu={handleSubmenuChange} />
            ) : currentSubmenu === 'twr' ? (
              <TWR />
            ) : currentSubmenu === 'margin' ? (
              <MarginSwitch />
            ) : currentSubmenu === 'risk' ? (
              <Risk />
            ) : (
              <EquityNew activeSubmenu={currentSubmenu} onLogout={onLogout} />
            )
          } />
          <Route path="/equity-new" element={<Navigate to="/equity" replace />} />
          <Route path="/open-positions" element={
            <OpenPositions
              username={username}
              onLogout={onLogout}
              activeSubmenu={currentSubmenu}
              setActiveSubmenu={handleSubmenuChange}
            />
          } />
          <Route path="/live-positions" element={
            <LiveOpenPositions username={username} onLogout={onLogout} />
          } />
          <Route path="/trades" element={
            currentSubmenu === 'covered-calls' ? (
              <CoveredCalls 
                username={username} 
                onLogout={onLogout} 
              />
            ) : (
              <Trades 
                username={username} 
                onLogout={onLogout} 
                activeSubmenu={currentSubmenu} 
                setActiveSubmenu={handleSubmenuChange} 
              />
            )
          } />
          <Route path="/tasty" element={
            <Tasty username={username} onLogout={onLogout} />
          } />
          <Route path="/trades2" element={
            <Trades2 
              username={username} 
              onLogout={onLogout} 
              activeSubmenu={currentSubmenu} 
              setActiveSubmenu={handleSubmenuChange} 
            />
          } />
          <Route path="/pmcc" element={<PMCC username={username} onLogout={onLogout} />} />
          <Route path="/pmcc/:id" element={<PMCC username={username} onLogout={onLogout} />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/risk" element={<Navigate to="/equity?submenu=risk" replace />} />
          <Route path="/documentation" element={
            currentSubmenu === 'etfs' ? <ETFs /> :
            currentSubmenu === 'itm-otm' ? <ITMOTMCalculation /> :
            currentSubmenu === 'informatie' ? (
              <SharedInfo onAttentionChange={refreshInfoAttention} />
            ) : (
              <ApiDocs />
            )
          } />
          <Route path="/api-docs" element={<Navigate to="/documentation?submenu=api" replace />} />
          <Route path="/tools" element={
            currentSubmenu === 'protective-covered-call' ? <ProtectiveCoveredCall /> : <OptionROI />
          } />
          <Route path="/tools/earnings" element={<Earnings />} />
          <Route path="/todo" element={<Todo username={username} onLogout={onLogout} />} />
          <Route path="/settings" element={
            <AccountDisplayNameManagement onClose={() => navigate('/equity')} />
          } />
          <Route path="/tags" element={
            <TagManagement onClose={() => navigate('/equity')} />
          } />
          <Route path="/tmp" element={<AdvancedOptionsDashboard />} />
          <Route path="/admin" element={<AdminRedirect />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default AppRouter;

